import classNames from 'classnames';
import { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';

import { Icon } from '../Icon';
import styles from './Modal.module.scss';

import logo from '@/assets/img/logo.png';
import { restaurantStore } from '@/entities/restaurant/store/restaurantStore';
import { useBodyScrollLock } from '@/shared/hooks/useBodyScrollLock';
import { imageLink } from '@/shared/lib';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  logoSrc?: string;
  fixedBar?: React.ReactNode;
  children: React.ReactNode;
  hideHeader?: boolean;
  closable?: boolean;
  fullScreen?: boolean;
  className?: string;
  contentClassName?: string;
  bgVariant?: 'grey' | 'white';
  prevStep?: boolean;
  onPrev?: () => void;
}

export const Modal: React.FC<ModalProps> = ({
  open,
  onClose,
  title,
  logoSrc = logo,
  fixedBar,
  children,
  hideHeader = false,
  closable = true,
  fullScreen = false,
  className,
  contentClassName,
  bgVariant = 'grey',
  prevStep = false,
  onPrev,
}: ModalProps) => {
  const [isVisible, setIsVisible] = useState(open);
  const [isClosing, setIsClosing] = useState(false);
  const [logoError, setLogoError] = useState(false);

  const address = restaurantStore.headerInfo?.address;

  useBodyScrollLock(open);

  useEffect(() => {
    if (open) {
      setIsVisible(true);
      setIsClosing(false);
    } else if (isVisible) {
      setIsClosing(true);
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [open, isVisible]);

  if (!isVisible) return null;

  return ReactDOM.createPortal(
    <div className={styles.backdrop} onClick={onClose}>
      <div
        className={classNames(styles.modal, className, {
          [styles.closing]: isClosing,
          [styles.fullScreen]: fullScreen,
          [styles.white]: bgVariant === 'white',
        })}
        onClick={(e) => e.stopPropagation()}
      >
        {!hideHeader && (title || (logoSrc && !logoError)) && (
          <div className={styles.header}>
            <div className={styles.headerInner}>
              <div className={styles.titleContainer}>
                {logoSrc && !logoError && (
                  <div className={styles.logo}>
                    <img
                      src={imageLink(logoSrc)}
                      alt="logo"
                      onError={() => setLogoError(true)}
                    />
                  </div>
                )}
                {title && <h2 className={styles.title}>{title}</h2>}
              </div>
              <h2 className={styles.subTitle}>{address}</h2>
            </div>
          </div>
        )}

        {closable && (
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
          >
            <Icon
              className={styles.icon}
              name="outlinedCancel"
              width={12}
              height={12}
            />
          </button>
        )}

        {prevStep && (
          <button
            type="button"
            className={classNames(styles.closeButton, styles.arrowLeft)}
            onClick={onPrev}
          >
            <Icon
              className={styles.icon}
              name="outlinedArrowLeft"
              width={12}
              height={12}
            />
          </button>
        )}

        <div className={classNames(styles.content, contentClassName)}>
          {children}
        </div>
        {fixedBar && <div className={styles.fixedBar}>{fixedBar}</div>}
      </div>
    </div>,
    document.body
  );
};
